/**
 * Format a number as currency (EUR)
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
  }).format(value);
}

/**
 * Generate initials from a name
 */
export function getInitials(name: string): string {
  return name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Format date for display
 */
export function formatDate(date: Date | number): string {
  const d = typeof date === "number" ? new Date(date) : date;
  return d.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/**
 * Format date and time for display
 */
export function formatDateTime(date: Date | number): string {
  const d = typeof date === "number" ? new Date(date) : date;
  return d.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Format duration in minutes to a readable string
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${mins}min`;
}

/**
 * Calculate date from a relative number (days from today)
 * Used for mail content templates
 * @param dateRelative - Number of days relative to today (negative = past, positive = future)
 * @param todayDate - Reference date (defaults to current date)
 */
export function calculateDateWithDateRelative(
  dateRelative: number,
  todayDate: Date = new Date(),
): string {
  const date = new Date(todayDate);
  date.setDate(date.getDate() + dateRelative);
  return date.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// 20 visually distinct colors spread across the hue wheel
const SENDER_COLOR_PALETTE = [
  "hsl(4, 70%, 55%)",
  "hsl(25, 70%, 52%)",
  "hsl(45, 65%, 48%)",
  "hsl(75, 55%, 48%)",
  "hsl(100, 55%, 48%)",
  "hsl(130, 50%, 48%)",
  "hsl(160, 55%, 46%)",
  "hsl(185, 60%, 47%)",
  "hsl(205, 65%, 52%)",
  "hsl(225, 60%, 55%)",
  "hsl(250, 55%, 57%)",
  "hsl(270, 55%, 55%)",
  "hsl(290, 55%, 52%)",
  "hsl(315, 60%, 52%)",
  "hsl(340, 65%, 52%)",
  "hsl(15, 60%, 52%)",
  "hsl(55, 60%, 46%)",
  "hsl(145, 50%, 46%)",
  "hsl(195, 65%, 48%)",
  "hsl(235, 58%, 56%)",
];

/**
 * Generate a deterministic background color from a sender name.
 * Picks from a palette of visually distinct colors so different
 * senders are unlikely to share the same color.
 */
export function getSenderColor(name: string): string {
  let hash = 5381;
  for (let i = 0; i < name.length; i++) {
    hash = ((hash << 5) + hash) ^ name.charCodeAt(i);
    hash |= 0;
  }
  return SENDER_COLOR_PALETTE[Math.abs(hash) % SENDER_COLOR_PALETTE.length]!;
}

/**
 * Replace variables in a template string
 */
export function replaceVariables(
  template: string,
  variables: Record<string, string | number | undefined>,
): string {
  return template.replace(/\{(\w+)\}/g, (_, key) => {
    const value = variables[key];
    return value !== undefined ? String(value) : `{${key}}`;
  });
}
